import React, { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormGroup,
  Label,
} from 'reactstrap';

import { Tenants } from 'enums';
import { OriginalRestaurant, TRestaurantFinancialConfigOrders } from 'types';

type CreateFinancialConfigModalProps = {
  restaurant: OriginalRestaurant;
  isOpen: boolean;
  onClose: () => void;
  onSave: (payload: TRestaurantFinancialConfigOrders) => void;
};

enum Step {
  SELECT_TENANT = 1,
  ORDERS_COMMISSION = 2,
  ONLINE_PAYMENTS_COMMISSION = 3,
}

const CreateFinancialConfigModal: React.FC<CreateFinancialConfigModalProps> = ({
  restaurant,
  isOpen,
  onClose,
  onSave,
}) => {
  const [currentStep, setCurrentStep] = useState<Step>(Step.SELECT_TENANT);
  const [selectedTenant, setSelectedTenant] = useState<Tenants | ''>('');
  const [ordersCommission, setOrdersCommission] = useState<string>('');
  const [onlinePaymentsCommission, setOnlinePaymentsCommission] = useState<string>('');

  const handleClose = () => {
    // Reset state when closing
    setCurrentStep(Step.SELECT_TENANT);
    setSelectedTenant('');
    setOrdersCommission('');
    setOnlinePaymentsCommission('');
    onClose();
  };

  const handleNext = () => {
    if (currentStep < Step.ONLINE_PAYMENTS_COMMISSION) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > Step.SELECT_TENANT) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    if (selectedTenant && ordersCommission && onlinePaymentsCommission) {
      const payload: TRestaurantFinancialConfigOrders = {
        commission: {
          [selectedTenant]: {
            orders: parseFloat(ordersCommission),
            online_payments: parseFloat(onlinePaymentsCommission),
          },
        },
        financial_report_id: {
          [selectedTenant]: '',
        },
      };
      onSave(payload);
      handleClose();
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case Step.SELECT_TENANT:
        return selectedTenant !== '';
      case Step.ORDERS_COMMISSION:
        return ordersCommission !== '' && !isNaN(parseFloat(ordersCommission));
      case Step.ONLINE_PAYMENTS_COMMISSION:
        return onlinePaymentsCommission !== '' && !isNaN(parseFloat(onlinePaymentsCommission));
      default:
        return false;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case Step.SELECT_TENANT:
        return 'Wybierz platformę';
      case Step.ORDERS_COMMISSION:
        return 'Podaj wysokość prowizji';
      case Step.ONLINE_PAYMENTS_COMMISSION:
        return 'Podaj wysokość prowizji od płatności online';
      default:
        return '';
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case Step.SELECT_TENANT:
        return (
          <FormGroup>
            <Label>Platforma:</Label>
            <Input
              type="select"
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value as Tenants)}
            >
              <option value="">Wybierz platformę</option>
              {Object.values(Tenants).map((tenant) => (
                <option key={tenant} value={tenant}>
                  {tenant}
                </option>
              ))}
            </Input>
          </FormGroup>
        );

      case Step.ORDERS_COMMISSION:
        return (
          <>
            <div className="mb-3">
              <div><strong>Restauracja:</strong> {restaurant.name}</div>
              <div><strong>Platforma:</strong> {selectedTenant}</div>
            </div>
            <FormGroup>
              <Label>Prowizja podstawowa od zamówień (%):</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={ordersCommission}
                onChange={(e) => setOrdersCommission(e.target.value)}
                placeholder="np. 15.50"
              />
            </FormGroup>
          </>
        );

      case Step.ONLINE_PAYMENTS_COMMISSION:
        return (
          <>
            <div className="mb-3">
              <div><strong>Restauracja:</strong> {restaurant.name}</div>
              <div><strong>Platforma:</strong> {selectedTenant}</div>
              <div><strong>Prowizja od zamówień:</strong> {ordersCommission}%</div>
            </div>
            <FormGroup>
              <Label>Prowizja od płatności online (%):</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={onlinePaymentsCommission}
                onChange={(e) => setOnlinePaymentsCommission(e.target.value)}
                placeholder="np. 2.50"
              />
            </FormGroup>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose}>
      <ModalHeader toggle={handleClose}>
        {getStepTitle()} ({currentStep}/3)
      </ModalHeader>
      <ModalBody>
        {renderStepContent()}
      </ModalBody>
      <ModalFooter>
        {currentStep > Step.SELECT_TENANT && (
          <Button color="secondary" onClick={handleBack}>
            Wstecz
          </Button>
        )}
        
        <Button color="secondary" onClick={handleClose}>
          Anuluj
        </Button>
        
        {currentStep < Step.ONLINE_PAYMENTS_COMMISSION ? (
          <Button 
            color="info" 
            onClick={handleNext}
            disabled={!isStepValid()}
          >
            Dalej
          </Button>
        ) : (
          <Button 
            color="success" 
            onClick={handleSubmit}
            disabled={!isStepValid()}
          >
            Utwórz
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default CreateFinancialConfigModal;
